import { useMemo } from 'react';
import { Badge } from '@antimatterhq/ui/components/badge';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableCell,
} from '@antimatterhq/ui/components/simpleTable';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import { Text } from '@antimatterhq/ui/components/typography';
import { DatasourceUpgradeStatusIcon } from '../../upgrade/statusIcon/DatasourceUpgradeStatusIcon';
import { cn } from '@/lib/utils';
import { DatasourceUpgradeFieldSpecDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { FieldSpecTableRow } from '@/components/table/FieldSpecTable/FieldSpecTableData';

export type FieldSpecReviewProps = {
  fields: FieldSpecTableRow[] | undefined;
  fieldUpgradeStates?: DatasourceUpgradeFieldSpecDiff[];
};

export const FieldSpecReview = ({
  fields = [],
  fieldUpgradeStates = [],
}: FieldSpecReviewProps) => {
  const fieldUpgradeStatesMap = useMemo(
    () =>
      fieldUpgradeStates.reduce(
        (acc, curr) => {
          // eslint-disable-next-line no-param-reassign
          acc[curr.fieldName] = curr;
          return acc;
        },
        {} as Record<string, DatasourceUpgradeFieldSpecDiff>,
      ),
    [fieldUpgradeStates],
  );

  if (!fields.length) {
    return (
      <Fieldset.Field>
        <Fieldset.Label></Fieldset.Label>
        <Fieldset.Value>
          <div className="w-[800px] truncate">
            <span className="italic">[none]</span>
          </div>
        </Fieldset.Value>
      </Fieldset.Field>
    );
  }

  return (
    <SimpleTable fixed>
      <SimpleTableHeader>
        <SimpleTableRow>
          <SimpleTableHead className="font-bold" width={300}>
            Name
          </SimpleTableHead>
          <SimpleTableHead className="font-bold" width={100}>
            Type
          </SimpleTableHead>
          <SimpleTableHead className="font-bold" width="auto">
            Definition
          </SimpleTableHead>
          {!!fieldUpgradeStates && (
            <SimpleTableHead className="font-bold" width={50}></SimpleTableHead>
          )}
        </SimpleTableRow>
      </SimpleTableHeader>
      <SimpleTableBody>
        {fields.map(
          ({ name, enabled, type, definition, source, strikethrough }) => (
            <SimpleTableRow key={name}>
              {enabled !== false && (
                <>
                  {(() => {
                    const upgradeStatus = fieldUpgradeStatesMap[name];
                    const shouldStrikethrough =
                      strikethrough ||
                      (upgradeStatus &&
                        !upgradeStatus.upgradePresetValue &&
                        !source.custom);

                    const strikethroughClass = shouldStrikethrough
                      ? 'line-through'
                      : '';

                    return (
                      <>
                        <SimpleTableCell
                          tooltip
                          truncate
                          className={cn(strikethroughClass)}
                          data-testid={`review-field-name-${name}`}
                        >
                          {name}
                        </SimpleTableCell>
                        <SimpleTableCell>
                          <Badge variant="filled">{type}</Badge>
                        </SimpleTableCell>
                        <SimpleTableCell
                          truncate
                          width="auto"
                          tooltip
                          className={cn(strikethroughClass)}
                          data-testid={`review-field-expression-${name}`}
                        >
                          {definition}
                        </SimpleTableCell>
                        {!!fieldUpgradeStates && (
                          <SimpleTableCell>
                            <DatasourceUpgradeStatusIcon
                              status={upgradeStatus?.status}
                            />
                          </SimpleTableCell>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              {enabled === false && (
                <>
                  <SimpleTableCell tooltip truncate>
                    <Text className="italic" color="secondary">
                      {name}
                    </Text>
                  </SimpleTableCell>
                  <SimpleTableCell colSpan={2}>
                    <Text className="italic" color="secondary">
                      (disabled)
                    </Text>
                  </SimpleTableCell>
                  {!!fieldUpgradeStates && <SimpleTableCell></SimpleTableCell>}
                </>
              )}
            </SimpleTableRow>
          ),
        )}
      </SimpleTableBody>
    </SimpleTable>
  );
};
